import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Table, LayoutList } from 'lucide-react';
import { dataEntryApi } from '../../services/api';
import DynamicModule from '../../components/common/DynamicModule';
import { useHelpKeyOverride } from '../../context/HelpContext';

import { entryMemoryKey, loadView, saveView } from '../../utils/viewMemory';
/**
 * DataEntryModulePage — runtime page for a Data Entry module.
 * Loads module config from AppCfg_DE_Modules and renders one tab per configured view.
 * Each view mounts DynamicModule locked to its view type with per-field entryMode overrides.
 *
 * Props (from the module's DataEntry navigation row):
 *   moduleSlug — matches Module_Slug in AppCfg_DE_Modules (e.g. "commissioning"); the row's TargetKey
 *   navKey     — the row's key; writes name it (X-Nav-Key) and buttons follow its capabilities
 */
const DataEntryModulePage = ({ moduleSlug, navKey }) => {
  // Footer help popup resolves this module's help row instead of the route slug
  useHelpKeyOverride(`dataentry:${moduleSlug}`);

  const [module, setModule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeViewIndex, setActiveViewIndex] = useState(0);
  // The active view tab is remembered against the history entry, so Back returns to the tab you
  // were on; a nav click is a new entry and starts on the first tab. Read through a ref so a
  // navigation never refetches the module.
  const location = useLocation();
  const tabMemoryKey = entryMemoryKey(location, moduleSlug, 'tab');
  const tabMemoryKeyRef = useRef(tabMemoryKey);
  tabMemoryKeyRef.current = tabMemoryKey;

  const loadModule = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await dataEntryApi.getModule(moduleSlug);
      setModule(res.data);
      setActiveViewIndex(loadView(tabMemoryKeyRef.current)?.tab ?? 0);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load module');
    } finally {
      setLoading(false);
    }
  }, [moduleSlug]);

  useEffect(() => { loadModule(); }, [loadModule]);

  // Own nav item clicked while here: a new entry, so back to the first tab (or the entry's tab).
  const lastTabKeyRef = useRef(tabMemoryKey);
  useEffect(() => {
    if (lastTabKeyRef.current === tabMemoryKey) return;
    lastTabKeyRef.current = tabMemoryKey;
    setActiveViewIndex(loadView(tabMemoryKey)?.tab ?? 0);
  }, [tabMemoryKey]);

  useEffect(() => {
    if (module) saveView(tabMemoryKey, { tab: activeViewIndex });
  }, [module, tabMemoryKey, activeViewIndex]);

  useEffect(() => {
    const handler = () => loadModule();
    window.addEventListener('pmd-database-changed', handler);
    return () => window.removeEventListener('pmd-database-changed', handler);
  }, [loadModule]);

  if (loading) return <div className="stui-module-loading">Loading...</div>;

  if (error) {
    return (
      <div className="stui-module-container">
        <div className="stui-module-error stui-text-error">
          {error}
        </div>
      </div>
    );
  }

  const views = module?.config?.views || [];

  if (views.length === 0) {
    return (
      <div className="stui-module-container">
        <div className="stui-module-empty">
          <p>{module?.module_Name || moduleSlug}</p>
          <p>No views configured. Use the Data Entry Composer to set up this module.</p>
        </div>
      </div>
    );
  }

  const sortedViews = [...views].sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
  const activeView = sortedViews[activeViewIndex] || sortedViews[0];

  // Build fieldOverrides from view's fieldConfig (entryMode per field)
  const buildFieldOverrides = (view) => {
    const overrides = {};
    (view.fieldConfig || []).forEach(fc => {
      if (fc.entryMode && fc.entryMode !== 'editable') {
        overrides[fc.fieldName] = { entryMode: fc.entryMode };
      }
    });
    return overrides;
  };

  const derivedDatasheetColumns = (activeView.fieldConfig || []).filter(fc => fc.fieldType === 'fk-derived');

  // Build externalModuleConfig from DEMC fieldConfig so DynamicModule uses the DEMC config
  // instead of the table's SVC JSON file. Handles both datasheet and list views.
  const buildExternalConfig = (view) => {
    const fieldConfig = view.fieldConfig || [];

    if (view.viewType === 'datasheet') {
      const allFields = fieldConfig.filter(fc => fc.visible !== false);
      const hasRegular = allFields.some(fc => fc.fieldType !== 'fk-derived');
      if (!hasRegular) return null;
      return {
        datasheetPanel: {
          columns: allFields.map(fc => ({
            fieldName: fc.fieldName,
            label: fc.label || fc.fieldName,
            width: fc.width || 150,
            alignment: fc.alignment || 'left',
            isDerived: fc.fieldType === 'fk-derived'
          }))
        }
      };
    }

    if (view.viewType === 'list') {
      if (fieldConfig.length === 0) return null;
      return {
        listPanel: view.listPanelConfig || {},
        detailPanel: { fields: fieldConfig }
      };
    }

    return null;
  };

  const headerTabs = sortedViews.length > 1 ? (
    <>
      {sortedViews.map((view, idx) => (
        <button
          key={view.viewId || idx}
          className={`stui-dataentry-tab${idx === activeViewIndex ? ' active' : ''}`}
          onClick={() => setActiveViewIndex(idx)}
        >
          {view.viewType === 'datasheet' ? <Table size={14} /> : <LayoutList size={14} />}
          <span>{view.viewLabel || view.tableName}</span>
        </button>
      ))}
    </>
  ) : null;

  return (
    <div className="stui-module-container">
      <DynamicModule
        key={`${moduleSlug}-${activeViewIndex}-${activeView.tableName}`}
        tableName={activeView.tableName}
        forceViewType={activeView.viewType}
        fieldOverrides={buildFieldOverrides(activeView)}
        externalModuleConfig={buildExternalConfig(activeView)}
        headerTabs={headerTabs}
        moduleContext="dataentry"
        navKey={navKey}
        viewMemoryId={`${moduleSlug}:${activeView.id ?? activeViewIndex}:${activeView.tableName}`}
        allowBulkDelete={false}
        derivedDatasheetColumns={derivedDatasheetColumns}
      />
    </div>
  );
};

export default DataEntryModulePage;
